import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import NetworkVisual from "@/components/NetworkVisual";
import ServiceIcon from "@/components/ServiceIcon";
import FAQAccordion from "@/components/FAQAccordion";
import CTASection from "@/components/CTASection";
import { GRCDiagram, ProcessDiagram } from "@/components/Diagrams";
import { COMPANY, HERITAGE_CLIENTS } from "@/config/company";
import { SERVICES, APPROACH_STAGES, CAPABILITIES, FAQS, WHY_NUMERIC, INDUSTRIES } from "@/data/site";

const HEADLINE = ["Strengthening Systems.", "Managing Risk.", "Building Resilience."];

function KineticHeadline() {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-white">
        {HEADLINE.join(" ")}
      </h1>
    );
  }
  return (
    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-white">
      {HEADLINE.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-1">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
          >
            {i === 2 ? <span className="text-teal">{line}</span> : line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

const TRUST_ITEMS = [
  { stat: "40+", label: "Years of collective team experience" },
  { stat: "ISO", label: "Management-system expertise" },
  { stat: "InfoSec", label: "Information security & cybersecurity advisory" },
  { stat: "FinServ", label: "Banking, fintech & enterprise experience" },
];

const HOME_SERVICES = [
  "iso-27001", "iso-22301", "iso-9001", "pci-dss", "iso-38500", "cyber-assessments",
  "swift-csp", "vapt", "vapt-remediation", "iso-training",
].map((id) => SERVICES.find((s) => s.id === id));

export default function Home() {
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);

  return (
    <>
      <SEO
        title={null}
        description="Numeric Consulting & Solutions helps organizations design, implement and strengthen management systems, information-security controls, cybersecurity practices and operational-resilience programmes."
        path="/"
      />

      {/* 1. Hero */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-navy via-navy-800 to-navy-900 overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 grid-pattern-dark" aria-hidden="true" />
        <div className="absolute -top-40 right-0 w-[36rem] h-[36rem] rounded-full bg-bright/10 blur-3xl" aria-hidden="true" />
        <div className="absolute bottom-0 -left-32 w-96 h-96 rounded-full bg-teal/10 blur-3xl" aria-hidden="true" />
        <div className="container-site relative pt-32 sm:pt-40 pb-16 sm:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div>
            <motion.p
              className="eyebrow-light mb-6"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.05, duration: 0.6 }}
            >
              Management Systems · Security · Resilience
            </motion.p>
            <KineticHeadline />
            <motion.p
              className="mt-6 text-base sm:text-lg leading-relaxed text-slate-300 max-w-xl"
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.7 }}
            >
              {COMPANY.name} helps organizations design, implement and strengthen management systems,
              information-security controls, cybersecurity practices and operational-resilience programmes.
            </motion.p>
            <motion.div
              className="mt-9 flex flex-col sm:flex-row gap-4"
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7 }}
            >
              <Link
                to="/contact"
                data-testid="hero-request-consultation-btn"
                className="inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-dark text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Request a Consultation
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-explore-services-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 hover:bg-white/5 text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Explore Our Services
              </Link>
            </motion.div>
          </div>
          <motion.div style={{ y: visualY }} className="relative" initial={reduce ? false : { opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.45, duration: 0.9 }}>
            <NetworkVisual />
          </motion.div>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-white border-b border-slate-200" data-testid="trust-strip">
        <div className="container-site py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {TRUST_ITEMS.map((item, i) => (
            <Reveal key={i} delay={i * 0.08} className="text-center lg:text-left">
              <p className="font-heading text-2xl sm:text-3xl font-extrabold text-corp">{item.stat}</p>
              <p className="mt-1.5 text-sm text-muted leading-snug">{item.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 3. Editorial marquee */}
      <Marquee items={CAPABILITIES} />

      {/* 4. Company introduction */}
      <section className="bg-mist py-16 sm:py-24" data-testid="home-intro">
        <div className="container-site grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="eyebrow mb-4">Who We Are</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Practical Expertise for Complex Risk and Compliance Environments
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted">
              {COMPANY.name} supports organizations seeking to strengthen governance, protect information
              assets, improve operational resilience and align their management systems with recognized
              international standards.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted">
              Our work connects five disciplines that are too often treated separately — governance, risk,
              compliance, security and resilience — into management systems that function in day-to-day
              operations, not only on audit day.
            </p>
            <Link
              to="/about"
              data-testid="intro-about-link"
              className="mt-7 inline-flex items-center gap-2 text-corp font-semibold hover:text-bright transition-colors"
            >
              Learn more about us <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
              <GRCDiagram />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5. Services overview */}
      <section className="bg-white py-16 sm:py-24" data-testid="home-services">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <p className="eyebrow mb-4">What We Do</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Consulting Services Built Around Recognized Standards
            </h2>
            <p className="mt-5 text-base sm:text-lg text-muted">
              From ISO management systems to technical security testing, every service is delivered with the
              same discipline: understand the business, address the risk, build lasting capability.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {HOME_SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <article className="group h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-lg hover:border-corp/30 hover:-translate-y-1 transition-[box-shadow,border-color,transform] duration-300" data-testid={`service-card-${s.id}`}>
                  <div className="w-12 h-12 rounded-xl bg-mist border border-slate-200 flex items-center justify-center text-corp group-hover:bg-corp group-hover:text-white transition-colors duration-300">
                    <ServiceIcon name={s.icon} />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-ink">{s.name}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted">{s.summary}</p>
                  <Link
                    to={`/services#${s.id}`}
                    data-testid={`service-learn-more-${s.id}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-corp group-hover:text-bright transition-colors"
                    aria-label={`Learn more about ${s.name}`}
                  >
                    Learn More <ArrowRight size={14} aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </article>
              </Reveal>
            ))}
            <Reveal delay={0.16}>
              <article className="group h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-lg hover:border-corp/30 hover:-translate-y-1 transition-[box-shadow,border-color,transform] duration-300" data-testid="service-card-app-dev-it">
                <div className="w-12 h-12 rounded-xl bg-mist border border-slate-200 flex items-center justify-center text-corp group-hover:bg-corp group-hover:text-white transition-colors duration-300">
                  <ServiceIcon name="development" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-ink">Application Development &amp; IT Consulting</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-muted">
                  Secure, scalable, business-aligned application development and experienced technology advisory, from solution evaluation to implementation guidance.
                </p>
                <Link
                  to="/services#app-development"
                  data-testid="service-learn-more-app-dev-it"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-corp group-hover:text-bright transition-colors"
                  aria-label="Learn more about application development and IT consulting"
                >
                  Learn More <ArrowRight size={14} aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 6. Why Numeric */}
      <section className="bg-mist py-16 sm:py-24" data-testid="why-numeric">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <p className="eyebrow mb-4">Why Numeric</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Experience That Shows Up in the Work
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_NUMERIC.map((w, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl bg-white border border-slate-200 p-7 flex gap-5">
                  <span className="font-mono text-sm font-semibold text-teal-dark shrink-0 pt-1" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-ink">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted">{w.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Consulting approach */}
      <section className="relative bg-gradient-to-br from-navy via-navy-800 to-navy-900 py-16 sm:py-24 overflow-hidden" data-testid="home-approach">
        <div className="absolute inset-0 grid-pattern-dark" aria-hidden="true" />
        <div className="container-site relative">
          <Reveal className="max-w-2xl">
            <p className="eyebrow-light mb-4">Our Consulting Approach</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              A Five-Stage Method for Lasting Results
            </h2>
          </Reveal>
          <div className="mt-14">
            <ProcessDiagram stages={APPROACH_STAGES} dark />
          </div>
          <Reveal className="mt-12 text-center">
            <Link
              to="/approach"
              data-testid="approach-learn-more-btn"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-white/50 hover:bg-white/5 text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
            >
              Explore Our Methodology <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 8. Standards & capabilities */}
      <section className="bg-white py-16 sm:py-24" data-testid="capabilities">
        <div className="container-site text-center">
          <Reveal className="max-w-2xl mx-auto">
            <p className="eyebrow mb-4">Standards &amp; Capabilities</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Frameworks We Work With Every Day
            </h2>
            <p className="mt-5 text-base sm:text-lg text-muted">
              Areas of consulting capability — presented as competencies, not company certifications.
            </p>
          </Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="capability-badges">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <span className="inline-flex items-center gap-2 rounded-full border border-corp/25 bg-mist px-5 py-2.5 text-sm font-semibold text-corp">
                  <CheckCircle2 size={15} className="text-teal-dark" aria-hidden="true" />
                  {c}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Industry experience */}
      <section className="bg-mist py-16 sm:py-24" data-testid="home-industries">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <p className="eyebrow mb-4">Industry Experience</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Environments Where Failure Is Not an Option
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.slice(0, 6).map((ind, i) => (
              <Reveal key={ind.id} delay={(i % 3) * 0.08}>
                <Link
                  to="/industries"
                  data-testid={`industry-card-${ind.id}`}
                  className="group block h-full rounded-2xl bg-white border border-slate-200 p-7 hover:shadow-lg hover:border-corp/30 hover:-translate-y-1 transition-[box-shadow,border-color,transform] duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-mist border border-slate-200 flex items-center justify-center text-corp group-hover:bg-corp group-hover:text-white transition-colors duration-300">
                    <ServiceIcon name={ind.icon} />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-ink">{ind.name}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted line-clamp-3">{ind.description}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Heritage */}
      <section className="bg-white py-16 sm:py-24" data-testid="heritage">
        <div className="container-site grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="eyebrow mb-4">Our Heritage</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Built on an Established Technology-Consulting Foundation
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted">
              {COMPANY.name} developed from the experience and capabilities of {COMPANY.parentName}, an IT
              consulting and third-party technology products and services company operating since {COMPANY.parentSince}.
              This foundation brings valuable understanding of technology environments, enterprise operations and
              the practical realities of implementing effective controls.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-slate-200 bg-mist p-7 sm:p-8">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-corp mb-5">
                {COMPANY.parentName} — Historic Client Base
              </h3>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="heritage-client-list">
                {HERITAGE_CLIENTS.map((c) => (
                  <li key={c} className="flex items-center gap-2.5 text-sm font-medium text-ink">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal shrink-0" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs leading-relaxed text-muted border-t border-slate-200 pt-4">
                Organizations listed represent the broader client history provided for {COMPANY.parentName}.
                Logos and endorsements are not implied.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 11. FAQ */}
      <section className="bg-mist py-16 sm:py-24" data-testid="home-faq">
        <div className="container-site grid lg:grid-cols-5 gap-12">
          <Reveal className="lg:col-span-2">
            <p className="eyebrow mb-4">Common Questions</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Frequently Asked Questions
            </h2>
            <p className="mt-5 text-base text-muted leading-relaxed">
              Straightforward answers about our services, standards and how engagements work.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-3">
            <FAQAccordion items={FAQS} />
          </Reveal>
        </div>
      </section>

      {/* 12. CTA */}
      <CTASection />
    </>
  );
}
